import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const MobileNav = () => {
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);

  // Primary navigation items (always visible)
  const primaryNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'LayoutDashboard', section: 'Overview' },
    { path: '/transaction-management', label: 'Transactions', icon: 'Receipt', section: 'Finances' },
    { path: '/investment-tracker', label: 'Investments', icon: 'TrendingUp', section: 'Planning' },
    { path: '/reports-analytics', label: 'Reports', icon: 'BarChart3', section: 'Overview' },
    { path: '/settings', label: 'More', icon: 'Menu', section: 'Settings', isMore: true }
  ];

  // Secondary navigation items (shown in "More" menu)
  const secondaryNavItems = [
    { path: '/recurring-bill-tracker', label: 'Bills', icon: 'FileText', section: 'Finances' },
    { path: '/retirement-calculator', label: 'Retirement', icon: 'PiggyBank', section: 'Planning' },
    { path: '/debt-payoff-calculator', label: 'Debt Payoff', icon: 'Calculator', section: 'Planning' },
    { path: '/systems-management', label: 'Systems', icon: 'Target', section: 'Growth' },
    { path: '/bank-connection', label: 'Bank Connect', icon: 'Building', section: 'Finances' },
    { path: '/receipt-scanner', label: 'Receipt Scan', icon: 'Camera', section: 'Finances' },
    { path: '/settings', label: 'Settings', icon: 'Settings', section: 'Settings' }
  ];

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const handleMoreClick = (e) => {
    e?.preventDefault();
    setShowMore(!showMore);
  };

  // Group secondary items by section
  const groupedSecondaryItems = secondaryNavItems?.reduce((acc, item) => {
    if (!acc?.[item?.section]) {
      acc[item?.section] = [];
    }
    acc?.[item?.section]?.push(item);
    return acc;
  }, {});

  return (
    <>
      {/* More Menu Overlay */}
      {showMore && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowMore(false)}
            aria-hidden="true"
          />
          <div className="fixed bottom-20 left-0 right-0 z-40 bg-white rounded-t-3xl shadow-2xl animate-slide-up max-h-[70vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">All Features</h3>
                <button
                  onClick={() => setShowMore(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close menu"
                >
                  <Icon name="X" size={20} color="#374151" strokeWidth={2} />
                </button>
              </div>
              
              {/* Grouped Navigation */}
              <div className="space-y-6">
                {Object.entries(groupedSecondaryItems)?.map(([section, items]) => (
                  <div key={section}>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                      {section}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {items?.map((item) => {
                        const active = isActive(item?.path);
                        return (
                          <Link
                            key={item?.path}
                            to={item?.path}
                            onClick={() => setShowMore(false)}
                            className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl transition-all duration-200 ${
                              active
                                ? 'text-green-700 bg-green-50 shadow-sm' 
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                          >
                            <Icon 
                              name={item?.icon} 
                              size={24} 
                              color="currentColor" 
                              strokeWidth={active ? 2.5 : 2}
                            />
                            <span className="text-xs font-medium text-center">{item?.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bottom Navigation Bar */}
      <nav 
        className="xl:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-2xl safe-area-inset-bottom"
        role="navigation" 
        aria-label="Mobile navigation"
      >
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          {primaryNavItems?.map((item) => {
            const active = isActive(item?.path);
            
            if (item?.isMore) {
              return (
                <button
                  key="more"
                  onClick={handleMoreClick}
                  className={`relative flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl transition-all duration-200 active:scale-95 min-h-[64px] focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${
                    showMore
                      ? 'text-green-700 bg-green-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  aria-label="More options"
                  aria-expanded={showMore}
                >
                  {showMore && (
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-green-600 rounded-full" aria-hidden="true" />
                  )}
                  <div className="relative">
                    <div className={`transition-transform duration-200 ${
                      showMore ? 'scale-110' : 'scale-100'
                    }`}>
                      <Icon 
                        name={item?.icon} 
                        size={24} 
                        color="currentColor" 
                        strokeWidth={showMore ? 2.5 : 2}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <span className={`text-xs font-medium leading-tight text-center transition-all duration-200 ${
                    showMore ? 'font-semibold' : 'font-normal'
                  }`}>
                    {item?.label}
                  </span>
                </button>
              );
            }
            
            return (
              <Link
                key={item?.path}
                to={item?.path}
                className={`relative flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl transition-all duration-200 active:scale-95 min-h-[64px] focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 ${
                  active
                    ? 'text-green-700 bg-green-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                aria-current={active ? 'page' : undefined}
                aria-label={item?.label}
              >
                {/* Active indicator */}
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-green-600 rounded-full" aria-hidden="true" />
                )}
                
                {/* Icon with badge container */}
                <div className="relative">
                  <div className={`transition-transform duration-200 ${
                    active ? 'scale-110' : 'scale-100'
                  }`}>
                    <Icon 
                      name={item?.icon} 
                      size={24} 
                      color="currentColor" 
                      strokeWidth={active ? 2.5 : 2}
                      aria-hidden="true"
                    />
                  </div>
                  {/* Notification badge for specific items - can be made dynamic */}
                  {item?.path === '/transaction-management' && (
                    <span 
                      className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" 
                      aria-label="New transactions available"
                    />
                  )}
                </div>
                
                {/* Label */}
                <span className={`text-xs font-medium leading-tight text-center transition-all duration-200 ${
                  active ? 'font-semibold' : 'font-normal'
                }`}>
                  {item?.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default MobileNav;